import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { VBox } from '../templates/layout/Flex';
import { Scrollbar } from '../atoms/Scrollbar';
import { Button } from '../atoms/Button';
import { cx } from '@emotion/css';

interface MasterDetailProps {
  listElement: ReactElement;
  detailElement: ReactElement;
  noticeElement?: ReactElement;
  listHeaderElement?: ReactElement;
  foldable?: boolean;
}
const listWidth = 320;
const useCSS = makeCSS(({ theme }) => ({
  root: {
    display: 'flex',
    flexFlow: 'row nowrap',
    height: '100%',
    width: '100%'
  },
  master: {
    display: 'flex',
    flexFlow: 'column nowrap',
    flexGrow: 0,
    flexShrink: 0,
    width: listWidth,
    borderRight: `1px solid ${theme.color.line['03']}`,
    padding: 16,
    position: 'relative',
    boxSizing: 'border-box'
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 42
  },
  notice: {},
  list: {
    width: listWidth,
    height: '100%',
    flexGrow: 1
  },
  listItem: {
    padding: '16px 8px',
    display: 'flex'
  },
  detail: {
    flexGrow: 1
  },
  active: {
    background: theme.color.overlay.focused
  }
}));
/** Usage:
 * - Each child element in `listElement` and `detailElement` should have a "data-id" defined as the matching key.
 */
export const MasterDetail = (props: MasterDetailProps) => {
  const {
    listHeaderElement,
    noticeElement,
    listElement,
    detailElement,
    foldable = false
  } = props;

  const classes = useCSS();
  const listHeaderRef = useRef<HTMLElement>(null);
  const noticeRef = useRef<HTMLElement>(null);

  /** Get the children of the `listElement` */
  const listChildren = useMemo(() => {
    return listElement.props.children as ReactElement[];
  }, [listElement]);

  /** Get the children of the `detailElement` */
  const detailChildren = useMemo(() => {
    return detailElement.props.children as ReactElement[];
  }, [detailElement]);

  const [selectedItem, setSelectedItem] = useState(listChildren[0]);

  /** Insert active state to each child element of `listElement` */
  const navItems = useMemo(() => {
    return React.Children.map(listChildren, (child) => {
      const active =
        (selectedItem as ReactElement)?.props['data-id'] ===
        child.props['data-id'];

      return React.cloneElement(child, {
        onClick: () => setSelectedItem(child),
        className: cx(active ? classes.active : '', classes.listItem)
      });
    });
  }, [listElement, selectedItem]);

  /** The detail of the selected item */
  const detail = useMemo(() => {
    return detailChildren.map((child) => {
      const active =
        (selectedItem as ReactElement).props['data-id'] ===
        child.props['data-id'];
      return React.cloneElement(child, {
        style: { display: active ? 'inherit' : 'none' }
      });
    });
  }, [detailChildren, selectedItem]);

  const [open, setOpen] = useState(true);

  const toggleSidebar = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  let top = 16;
  let listHeaderHeight = 16;
  if (listHeaderRef.current) {
    listHeaderHeight += listHeaderRef.current.getBoundingClientRect().height;
    top += listHeaderHeight;
  }
  if (noticeRef.current) {
    top += noticeRef.current.getBoundingClientRect().height;
  }

  return (
    <div className={classes.root}>
      <div
        className={classes.master}
        style={{
          width: open ? listWidth : 68,
          transition: 'width 0.3s ease-in-out'
        }}
      >
        {(foldable || listHeaderElement) && (
          <header className={classes.listHeader} ref={listHeaderRef}>
            {listHeaderElement && (
              <div
                style={{
                  position: 'absolute',
                  left: open ? 16 : -listWidth,
                  transition: 'left 0.3s ease-in-out'
                }}
              >
                {listHeaderElement}
              </div>
            )}
            {foldable && (
              <div style={{ position: 'absolute', right: 14 }}>
                <Button
                  iconLeft={
                    open ? 'view_sidebar_left' : 'view_sidebar_left_fill'
                  }
                  onClick={toggleSidebar}
                  background="secondary"
                  buttonStyle="ghost"
                  extraRootProps={{ 'data-prevent-deselect': true }}
                />
              </div>
            )}
          </header>
        )}
        <div
          style={{
            position: 'absolute',
            height: `calc(100% - ${listHeaderHeight}px)`,
            top: top,
            right: open ? 0 : listWidth,
            transition: 'right 0.3s ease-in-out'
          }}
        >
          <aside className={classes.notice} ref={noticeRef}>
            {noticeElement}
          </aside>
          <nav className={classes.list}>
            <Scrollbar>
              <VBox>{navItems}</VBox>
            </Scrollbar>
          </nav>
        </div>
      </div>
      <div className={classes.detail}>
        <Scrollbar>{detail}</Scrollbar>
      </div>
    </div>
  );
};
